import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, Compass } from "lucide-react";
import { Helmet } from 'react-helmet-async';

// Images
import Madayipara from "../images/Madayipara Payangadi Live.webp";
import Madaikotta from "../images/Madayikkotta Payangadi Live.webp";
import Madaipalli from "../images/Madayippalli Payangadi Live.webp";
import Vadukunda from "../images/Vadukunda Temple Payangadi Live.webp";
import Jewpond from "../images/Joothakkulam Payangadi Live.webp";
import Kakki from "../images/kakkithodu.jpg";
import Vayalapra from "../images/Vayalapra Parappu Payangadi Live.webp";
import Chootad from "../images/Choottad Beach Payangadi Live.webp";
import Mattul from "../images/mattolbecah.webp";
import Madaikavu from "../images/madayikavu.jpg";
import Riverview from "../images/Payangadi Reverview Park.webp";

export default function Tourism() {
  const navigate = useNavigate();

  const places = [
    { id: "madaipara", name: "മാടായിപ്പാറ", desc: "പ്രകൃതി സൗന്ദര്യം നിറഞ്ഞ കുന്ന് പ്രദേശം", image: Madayipara },
    { id: "madaikotta", name: "മാടായിക്കോട്ട", desc: "ചരിത്ര പ്രാധാന്യം നിറഞ്ഞയിടം", image: Madaikotta },
    { id: "madaipalli", name: "മാടായി പള്ളി", desc: "പ്രാചീന മുസ്ലിം പള്ളി", image: Madaipalli },
    { id: "vadukunda", name: "വടുകുന്ദ ശിവക്ഷേത്രം", desc: "പ്രശസ്ത ശിവക്ഷേത്രം", image: Vadukunda },
    { id: "jewpond", name: "ജൂതക്കുളം", desc: "ജൂത ചരിത്രവുമായി ബന്ധപ്പെട്ട സ്ഥലം", image: Jewpond },
    { id: "kakki", name: "കക്കി തോട്", desc: "മഴക്കാല വെള്ളച്ചാട്ടം", image: Kakki },
    { id: "vayalapra", name: "വയലപ്ര പരപ്പ്", desc: "വർഷം തോറും ആയിരക്കണക്കിന് ദേശാടനപ്പക്ഷികളെയാണ് ഈ ചതുപ്പ് പ്രദേശം ആകർഷിക്കുന്നത്", image: Vayalapra },
    { id: "chootad", name: "ചൂട്ടാട് ബീച്ച്", desc: "ചൂട്ടാട് ബീച്ച് പാർക്കും കുട്ടികൾക്കായുണ്ട്", image: Chootad },
    { id: "mattul", name: "മാട്ടൂൽ ബീച്ച്", desc: "ഏഴിമലയ്ക്ക് സമീപം", image: Mattul },
    { id: "madaikavu", name: "മാടായി കാവ്", desc: "ഉത്തരകേരളത്തിലെ ഏറ്റവും പ്രസിദ്ധമായ ഭദ്രകാളിക്ഷേത്രം", image: Madaikavu },
    { id: "riverview", name: "റിവർ വ്യൂ പാർക്ക്", desc: "ഇപ്പോൾ തകർച്ചയിലാണ് ഇത്", image: Riverview },
  ];

  return (
    <>
      <Helmet>
        <title>Tourism in Kalliasseri | Info Payangadi</title>
        <meta name="description" content="കല്യാശ്ശേരിയിലെ പ്രധാന വിനോദസഞ്ചാര കേന്ദ്രങ്ങളായ മാടായിപ്പാറ, ചൂട്ടാട് ബീച്ച്, വയലപ്ര തുടങ്ങിയവയുടെ വിവരങ്ങൾ." />
      </Helmet>

      <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-6 pb-28 selection:bg-blue-200">
        
        {/* 🌟 PREMIUM HEADER 🌟 */}
        <div className="flex flex-col items-center justify-center text-center mb-10 mt-6 relative z-10">
          <motion.div 
            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
            className="bg-blue-100 p-4 rounded-3xl text-blue-600 mb-5 shadow-sm"
          >
            <Compass size={32} strokeWidth={2} />
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-800 tracking-tight"
          >
            Explore <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Tourism</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-slate-500 mt-2.5 font-medium text-sm sm:text-base max-w-md mx-auto"
          >
            കല്യാശ്ശേരിയിലെ മനോഹരമായ വിനോദസഞ്ചാര കേന്ദ്രങ്ങൾ
          </motion.p>
        </div>

        {/* 🌟 TOURISM GRID 🌟 */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto relative z-10">
          {places.map((place, i) => (
            <motion.div
              key={i}
              onClick={() => navigate(`/tourism/${place.id}`)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.05, duration: 0.5, ease: "easeOut" }}
              whileHover={{ y: -8 }}
              className="group bg-white rounded-[2rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(37,99,235,0.12)] border border-slate-100 overflow-hidden cursor-pointer flex flex-col transition-all duration-300"
            >
              {/* Image Section with inner padding effect */}
              <div className="p-3 pb-0">
                <div className="rounded-[1.5rem] overflow-hidden relative">
                  {/* Subtle Dark Gradient Overlay for premium look */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10 pointer-events-none"></div>
                  
                  <img 
                    src={place.image} 
                    alt={place.name}
                    className="w-full h-56 sm:h-64 object-cover transform group-hover:scale-110 transition-transform duration-700 ease-in-out" 
                  />
                  
                  {/* Floating Tag */}
                  <div className="absolute top-4 left-4 z-20 bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-sm">
                    <MapPin size={12} className="text-emerald-500" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">Tourism</span>
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl sm:text-2xl font-black text-slate-800 mb-2 group-hover:text-blue-600 transition-colors">
                  {place.name}
                </h3>
                <p className="text-sm text-slate-500 font-medium leading-relaxed line-clamp-2 mb-4 flex-grow">
                  {place.desc}
                </p>

                {/* Explore Button / Chevron */}
                <div className="flex items-center gap-2 mt-auto pt-4 border-t border-slate-50">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-wider group-hover:text-blue-700 transition-colors">
                    View Details
                  </span>
                  <div className="bg-blue-50 p-1.5 rounded-full text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300 transform group-hover:translate-x-1">
                    <ChevronRight size={16} strokeWidth={3} />
                  </div>
                </div>
              </div>

            </motion.div>
          ))}
        </div>

      </div>
    </>
  );
}